import {
  AIModel,
  ChatCompletionRequest,
  ChatCompletionResponse,
  ModelProvider,
  ModelsAPIResponse,
} from '../types/models';
import { EmbeddingModel } from '../types/fileUpload';
import { aiAPI } from './api/aiApi';
import http from '../lib/api';

class ModelService {
  private cache: Map<string, AIModel[]> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly API_BASE_URL = process.env.REACT_APP_API_URL || '';

  private isCacheValid(key: string): boolean {
    const expiry = this.cacheExpiry.get(key);
    return expiry ? Date.now() < expiry : false;
  }

  private setCache(key: string, data: AIModel[]): void {
    this.cache.set(key, data);
    this.cacheExpiry.set(key, Date.now() + this.CACHE_DURATION);
  }

  async getModels(params?: {
    provider?: string;
    model_type?: string;
    is_active?: boolean;
    include_public?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<AIModel[]> {
    const cacheKey = JSON.stringify(params || {});

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey) || [];
    }

    try {
      // Try the current API endpoint first
      const response = await aiAPI.getModels();
      let models = response.data as any;

      // If we get a ModelsAPIResponse structure, extract the models array
      if (models && typeof models === 'object' && 'models' in models) {
        models = (models as ModelsAPIResponse).models;
      }

      // Ensure we have an array
      if (!Array.isArray(models)) {
        throw new Error('API response is not an array of models');
      }

      // Transform API AIModel to models.ts AIModel format
      const transformedModels: AIModel[] = models.map((model: any) => ({
        id: model.id,
        name: model.name,
        display_name: model.display_name || model.name,
        api_name: model.api_name || model.name,
        supports_thinking: model.supports_thinking ?? false,
        is_default: model.is_default ?? false,
        provider: model.provider,
        model_type: model.model_type,
        base_url: model.base_url,
        description: model.description,
        context_window: model.context_window,
        is_active: model.is_active,
        is_public: model.is_public,
        owner_id: model.owner_id,
        created_at: model.created_at,
        updated_at: model.updated_at,
        params: model.params,
        metadata: model.metadata,
        capabilities: model.capabilities,
        usage_count: model.usage_count,
      }));

      this.setCache(cacheKey, transformedModels);
      return transformedModels;
    } catch (error) {
      // If API fails, try to return cached data even if expired
      const cachedModels = this.cache.get(cacheKey);
      if (cachedModels) {
        return cachedModels;
      }

      // If aiAPI fails, throw the error (api.ts already handles refresh logic)
      throw new Error(
        `Failed to fetch models: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async getOllamaModels(base_url?: string): Promise<AIModel[]> {
    const cacheKey = `ollama_${base_url || 'default'}`;

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey) || [];
    }

    try {
      const response = await aiAPI.getModels();
      const allModels = response.data as any;
      const ollamaModels = allModels.filter((model: any) => model.provider === 'ollama');

      this.setCache(cacheKey, ollamaModels);
      return ollamaModels;
    } catch (error) {
      // If discovery fails, try to return cached data
      const cachedModels = this.cache.get(cacheKey);
      if (cachedModels) {
        return cachedModels;
      }
      throw error;
    }
  }

  async discoverOllamaModels(base_url?: string): Promise<AIModel[]> {
    try {
      // Call the discovery endpoint using api
      const response = await http.post('/api/v1/models/discover/ollama', { base_url });
      const discoveryData = response.data;

      // Handle both direct array and ModelsAPIResponse formats
      let ollamaModels: any[] = [];
      if (Array.isArray(discoveryData)) {
        ollamaModels = discoveryData;
      } else if (
        discoveryData &&
        typeof discoveryData === 'object' &&
        'models' in discoveryData &&
        Array.isArray((discoveryData as any).models)
      ) {
        ollamaModels = (discoveryData as any).models;
      } else {
        throw new Error('Invalid response format from Ollama discovery endpoint');
      }

      // Convert Ollama models to our AIModel format
      const models: AIModel[] = ollamaModels.map(ollamaModel => ({
        id: `ollama_${ollamaModel.name}`,
        name: ollamaModel.name,
        display_name: this.formatDisplayName(ollamaModel.name),
        api_name: ollamaModel.name, // Use name as api_name
        supports_thinking: false, // Ollama models don't support thinking mode by default
        is_default: false, // Not default by default
        provider: 'ollama' as const,
        model_type: 'chat' as const,
        base_url: base_url || 'http://localhost:11434',
        description: `${ollamaModel.details.parameter_size} parameters`,
        context_window: this.estimateContextWindow(ollamaModel),
        is_active: true,
        is_public: true,
        owner_id: 0, // System owned
        created_at: ollamaModel.modified_at,
        updated_at: ollamaModel.modified_at,
        params: {
          temperature: 0.7,
          top_p: 0.9,
          max_tokens: 2048,
        },
        metadata: {
          size: ollamaModel.size,
          digest: ollamaModel.digest,
          family: ollamaModel.details.family,
          format: ollamaModel.details.format,
          quantization_level: ollamaModel.details.quantization_level,
        },
        capabilities: this.getCapabilities(ollamaModel),
      }));

      // Update cache
      const cacheKey = `ollama_${base_url || 'default'}`;
      this.setCache(cacheKey, models);

      return models;
    } catch (error) {
      throw new Error(
        `Failed to discover Ollama models: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  private formatDisplayName(name: string): string {
    return name
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private estimateContextWindow(model: any): number {
    const family = model.details.family.toLowerCase();
    const size = model.details.parameter_size;

    // Rough estimates based on model family and size
    if (family.includes('llama') || family.includes('codellama')) {
      if (size.includes('70B')) return 4096;
      if (size.includes('13B')) return 4096;
      if (size.includes('7B')) return 4096;
      if (size.includes('34B')) return 8192;
      return 2048;
    }

    if (family.includes('mistral') || family.includes('mixtral')) {
      return 32768;
    }

    if (family.includes('qwen')) {
      return 8192;
    }

    return 2048; // Default fallback
  }

  private getCapabilities(model: any): string[] {
    const capabilities = ['chat'];
    const family = model.details.family.toLowerCase();

    if (family.includes('codellama')) {
      capabilities.push('code');
    }

    if (family.includes('llava')) {
      capabilities.push('vision');
    }

    return capabilities;
  }

  async getModelById(id: string): Promise<AIModel | null> {
    try {
      const response = await aiAPI.getModels();
      const models = response.data as any;
      const foundModel = models.find((model: any) => model.id === id) || null;

      if (!foundModel) return null;

      // Transform to AIModel format
      return {
        id: foundModel.id,
        name: foundModel.name,
        display_name: foundModel.display_name || foundModel.name,
        api_name: foundModel.api_name || foundModel.name,
        supports_thinking: foundModel.supports_thinking ?? false,
        is_default: foundModel.is_default ?? false,
        provider: foundModel.provider,
        description: foundModel.description,
        context_window: foundModel.context_window,
        params: foundModel.params,
        metadata: foundModel.metadata,
        capabilities: foundModel.capabilities,
        usage_count: foundModel.usage_count,
      };
    } catch (error) {
      throw new Error(
        `Failed to get model ${id}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async createModel(modelData: Partial<AIModel>): Promise<AIModel> {
    try {
      const response = await aiAPI.createModel(modelData);

      // Clear cache to force refresh
      this.cache.clear();
      this.cacheExpiry.clear();

      return response.data as AIModel;
    } catch (error) {
      throw new Error(
        `Failed to create model: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async updateModel(id: string, modelData: Partial<AIModel>): Promise<AIModel> {
    try {
      const response = await aiAPI.updateModel(id, modelData);

      // Clear cache to force refresh
      this.cache.clear();
      this.cacheExpiry.clear();

      return response.data as AIModel;
    } catch (error) {
      throw new Error(
        `Failed to update model ${id}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async deleteModel(id: string): Promise<void> {
    try {
      await aiAPI.deleteModel(id);

      // Clear cache to force refresh
      this.cache.clear();
      this.cacheExpiry.clear();
    } catch (error) {
      throw new Error(
        `Failed to delete model ${id}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async getProviders(): Promise<ModelProvider[]> {
    try {
      const response = await aiAPI.getProviders();
      const providers = response.data as any[];

      // Transform API AIProvider to ModelProvider format
      return providers.map(provider => ({
        name: provider.name || provider.id,
        display_name: provider.display_name || provider.name,
        type: provider.type || 'api',
        description: provider.description,
        base_url: provider.base_url,
        supported_features: provider.supported_features || [],
        icon_url: provider.icon_url,
      }));
    } catch (error) {
      // Return default providers if API fails
      return [
        {
          name: 'ollama',
          display_name: 'Ollama',
          type: 'local',
          description: 'Run models locally with Ollama',
          base_url: 'http://localhost:11434',
          supported_features: ['chat', 'streaming'],
        },
        {
          name: 'openai',
          display_name: 'OpenAI',
          type: 'api',
          description: 'OpenAI API models',
          supported_features: ['chat', 'streaming', 'function-calling'],
        },
      ];
    }
  }

  /**
   * 获取可用的embedding模型列表
   */
  async getEmbeddingModels(): Promise<EmbeddingModel[]> {
    try {
      const response = await http.get('/api/v1/models/embedding');
      return (response.data as any) || [];
    } catch (error) {
      console.error('Failed to fetch embedding models:', error);
      return [];
    }
  }

  async testModel(
    modelId: string,
    message: string
  ): Promise<{ response: string; latency: number }> {
    try {
      const response = await aiAPI.testModel(modelId, message);
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to test model ${modelId}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  clearCache(): void {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  // Chat completion methods
  async createChatCompletion(request: ChatCompletionRequest): Promise<ChatCompletionResponse> {
    try {
      // Convert ChatCompletionRequest to ChatRequest format
      const chatRequest = {
        modelId: request.model,
        messages: request.messages.map(msg => ({
          id: `msg-${Date.now()}-${Math.random()}`,
          conversationId: 'temp', // This is for model completion, not needed for actual chat
          content: msg.content,
          role: msg.role,
          timestamp: msg.timestamp?.toString() || new Date().toISOString(),
          metadata: request.params, // Use request params as metadata instead
        })),
        temperature: request.temperature,
        maxTokens: request.max_tokens,
        stream: false,
      };
      const response = await aiAPI.chat(chatRequest);

      // Transform ChatResponse to ChatCompletionResponse format
      const chatResponse = response.data as any;
      return {
        id: chatResponse.id || 'cmpl-' + Date.now(),
        object: 'chat.completion',
        created: Math.floor(Date.now() / 1000),
        model: request.model,
        choices: [
          {
            index: 0,
            message: {
              role: chatResponse.message?.role || 'assistant',
              content: chatResponse.message?.content || '',
            },
            finish_reason: chatResponse.finishReason || 'stop',
          },
        ],
        usage: chatResponse.usage
          ? {
              prompt_tokens: chatResponse.usage.promptTokens,
              completion_tokens: chatResponse.usage.completionTokens,
              total_tokens: chatResponse.usage.totalTokens,
            }
          : undefined,
      };
    } catch (error) {
      throw new Error(
        `Chat completion failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async streamChatCompletion(
    request: ChatCompletionRequest,
    onChunk: (chunk: string) => void
  ): Promise<void> {
    try {
      // Convert ChatCompletionRequest to ChatRequest format
      const chatRequest = {
        modelId: request.model,
        messages: request.messages.map(msg => ({
          id: `msg-${Date.now()}-${Math.random()}`,
          conversationId: 'temp', // This is for model completion, not needed for actual chat
          content: msg.content,
          role: msg.role,
          timestamp: msg.timestamp?.toString() || new Date().toISOString(),
          metadata: request.params, // Use request params as metadata instead
        })),
        temperature: request.temperature,
        maxTokens: request.max_tokens,
        stream: true,
      };
      await aiAPI.streamChat(chatRequest, onChunk);
    } catch (error) {
      throw new Error(
        `Streaming chat completion failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}

export const modelService = new ModelService();
export default modelService;
